import { numberWithCommas } from '../../../../utils/number';
import { useBtcPrice } from '../../../../hooks/usePricing';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../ui/tooltip';
import { Skeleton } from '../../../ui/skeleton';
import { Info } from 'lucide-react';
import { NormalizedAddressStats } from 'src/hooks/useAddressData';

interface AddressStatsProps {
  stats: NormalizedAddressStats | null | undefined;
  isLoading?: boolean;
  error?: Error | null;
}

// Pure functions for calculations
const calculateStatsDisplay = (
  stats: NormalizedAddressStats,
  btcPrice: number,
) => {
  if (stats.chain === 'bitcoin') {
    return {
      symbol: 'BTC',
      pricePerUnit: btcPrice,
      totalReceivedUsd: calculateUsdValue(stats.totalReceived, btcPrice),
      totalSentUsd: calculateUsdValue(stats.totalSent, btcPrice),
    };
  }

  // EVM chain
  return {
    symbol: stats.symbol,
    pricePerUnit: stats.priceUsd,
    totalReceivedUsd: calculateUsdValue(stats.totalReceived, stats.priceUsd),
    totalSentUsd: calculateUsdValue(stats.totalSent, stats.priceUsd),
  };
};

const calculateUsdValue = (amount: number, price: number): string => {
  return price > 0 ? numberWithCommas(amount * price, 2) : 'N/A';
};

const formatCryptoAmount = (amount: number): string => {
  return numberWithCommas(amount, 8);
};

export const AddressStats = ({
  stats,
  isLoading,
  error,
}: AddressStatsProps) => {
  const { btcPrice } = useBtcPrice();

  if (isLoading) {
    return (
      <div className="grid w-full grid-cols-1 gap-2 sm:grid-cols-3">
        {[...Array(3)].map((_, i) => (
          <Skeleton
            key={i}
            className="h-[108px] w-full rounded-lg bg-card p-4"
          />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="grid w-full grid-cols-1 gap-2 sm:grid-cols-3">
        <div className="col-span-3 rounded-lg bg-card p-4 text-center text-sm text-muted-foreground">
          Unable to load stats: {error.message}
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="grid w-full grid-cols-1 gap-2 sm:grid-cols-3">
        <div className="col-span-3 rounded-lg bg-card p-4 text-center text-sm text-muted-foreground">
          No statistics available
        </div>
      </div>
    );
  }

  const { symbol, totalReceivedUsd, totalSentUsd } = calculateStatsDisplay(
    stats,
    btcPrice,
  );

  const displayData = {
    totalReceivedCrypto: formatCryptoAmount(stats.totalReceived),
    totalSentCrypto: formatCryptoAmount(stats.totalSent),
    txCount: numberWithCommas(stats.txCount),
    firstSeen: stats.firstSeen ?? 'N/A',
  };

  return (
    <TooltipProvider>
      <div className="grid w-full grid-cols-1 gap-2 sm:grid-cols-3">
        <StatCard
          title="Total Received"
          value={`${displayData.totalReceivedCrypto} ${symbol}`}
          subValue={totalReceivedUsd}
          subValueType="price"
          tooltip={`Total amount of ${symbol} received by this address`}
        />

        <StatCard
          title="Total Sent"
          value={`${displayData.totalSentCrypto} ${symbol}`}
          subValue={totalSentUsd}
          subValueType="price"
          tooltip={`Total amount of ${symbol} sent from this address`}
        />

        <StatCard
          title="Transactions"
          value={displayData.txCount}
          subValue={displayData.firstSeen}
          subValueType="date"
          tooltip="Total number of transactions associated with this address"
          subValueTooltip={
            displayData.firstSeen !== 'N/A'
              ? 'First seen date: The date when this address was first used in a transaction (yyyy-mm-dd format)'
              : undefined
          }
        />
      </div>
    </TooltipProvider>
  );
};

type StatCardProps = {
  title: string;
  value: string;
  subValue: string;
  subValueType: 'price' | 'date';
  tooltip: string;
  subValueTooltip?: string;
};

export const StatCard = ({
  title,
  value,
  subValue,
  subValueType,
  tooltip,
  subValueTooltip,
}: StatCardProps) => (
  <div className="h-full rounded-lg bg-card p-4 shadow-sm">
    <div className="mb-2 flex items-center gap-1 text-sm text-foreground/80">
      {title}
      <Tooltip>
        <TooltipTrigger>
          <Info className="h-3 w-3" />
        </TooltipTrigger>
        <TooltipContent>
          <p>{tooltip}</p>
        </TooltipContent>
      </Tooltip>
    </div>
    <div className="truncate font-mono text-lg font-semibold text-foreground">
      {value}
    </div>
    <div className="flex items-center gap-1 truncate font-mono text-sm text-foreground/70">
      {subValueType === 'price' ? `$${subValue}` : subValue}
      {subValueType === 'date' && subValueTooltip && (
        <Tooltip>
          <TooltipTrigger>
            <Info className="h-3 w-3 flex-shrink-0" />
          </TooltipTrigger>
          <TooltipContent>
            <p>{subValueTooltip}</p>
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  </div>
);
